import { MedicationEvent, ScheduleCalendarEvent } from '../types';

export const SLOT_MINUTES = 15;

/** Events shown side-by-side in one slot before the rest collapse into "+N". */
export const MAX_EVENTS_PER_SLOT = 3;

const SLOT_MS = SLOT_MINUTES * 60 * 1000;

export const slotStartFor = (date: Date): Date => {
  const slotStart = new Date(date);
  slotStart.setMinutes(
    Math.floor(slotStart.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES,
    0,
    0,
  );
  return slotStart;
};

export const overflowIdFor = (slotStart: Date): string =>
  `overflow-${slotStart.getTime()}`;

/**
 * react-big-calendar moves any event whose start and end fall on different
 * dates into the all-day row (TimeGrid.render), which this calendar hides — so
 * the 23:45 slot must end before midnight or its events vanish. Costs the last
 * row of the day ~3px of height, since rbc positions on whole minutes.
 */
const slotEndFor = (slotStart: Date, rawEnd: number): Date => {
  const endOfDay = new Date(slotStart);
  endOfDay.setHours(23, 59, 59, 999);
  return new Date(Math.min(rawEnd, endOfDay.getTime()));
};

/**
 * Buckets med events into 15-minute slots so the calendar can render each block
 * snapped to a single row, with the events of a slot laid out side-by-side.
 * Events past MAX_EVENTS_PER_SLOT are handed to a trailing "+N" block.
 */
export const buildCalendarEvents = (
  events: MedicationEvent[],
): ScheduleCalendarEvent[] => {
  const slots = new Map<number, MedicationEvent[]>();

  events.forEach((event) => {
    const start = new Date(event.start);
    if (isNaN(start.getTime())) return;

    const key = slotStartFor(start).getTime();
    const slotEvents = slots.get(key);
    if (slotEvents) {
      slotEvents.push(event);
    } else {
      slots.set(key, [event]);
    }
  });

  const calendarEvents: ScheduleCalendarEvent[] = [];

  slots.forEach((slotEvents, key) => {
    const slotStart = new Date(key);
    const slotEnd = slotEndFor(slotStart, key + SLOT_MS);
    const ordered = [...slotEvents].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    );

    ordered.slice(0, MAX_EVENTS_PER_SLOT).forEach((medEvent) => {
      calendarEvents.push({
        id: String(medEvent.id),
        slotStart,
        slotEnd,
        medEvent,
        overflowEvents: [],
      });
    });

    const overflowEvents = ordered.slice(MAX_EVENTS_PER_SLOT);
    if (overflowEvents.length) {
      calendarEvents.push({
        id: overflowIdFor(slotStart),
        slotStart,
        slotEnd,
        medEvent: null,
        overflowEvents,
      });
    }
  });

  return calendarEvents.sort(
    (a, b) => a.slotStart.getTime() - b.slotStart.getTime(),
  );
};
